import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Info, ShieldCheck, RefreshCw, Wallet, Landmark, CircleDollarSign, PencilLine } from "lucide-react";
import { api, inr, fmtDate, fmtDateTime, apiError } from "@/lib/api";
import { Skeleton } from "@/components/common";
import { CoinIcon } from "@/components/CoinBadge";

const TYPE_META = {
  EMI: { label: "Pay EMI", icon: Wallet, desc: "Your current EMI instalment" },
  OVERDUE: { label: "Pay Overdue / Outstanding", icon: CircleDollarSign, desc: "Clear pending overdue amount" },
  POS: { label: "Pay POS", icon: Landmark, desc: "Principal outstanding (lender-provided)" },
  CUSTOM: { label: "Custom Amount", icon: PencilLine, desc: "Pay any amount you choose" },
};

const naFmt = (v, money = true) => (v === null || v === undefined ? "Not available from lender" : money ? inr(v) : String(v));

export default function PayLoan() {
  const { state } = useLocation();
  const nvg = useNavigate();
  const loanState = state?.loan;
  const [d, setD] = useState(null);
  const [sel, setSel] = useState("EMI");
  const [custom, setCustom] = useState("");
  const [method, setMethod] = useState("UPI_INTENT");
  const [outcome, setOutcome] = useState("success");
  const [useWallet, setUseWallet] = useState(false);
  const [useCoins, setUseCoins] = useState(false);
  const [useRewardCash, setUseRewardCash] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loanState?.id) { nvg("/loans"); return; }
    api.get(`/loans/${loanState.id}/pay-options`).then((r) => setD(r.data)).catch(() => setD(null));
  }, [loanState, nvg]);

  if (!loanState?.id) return null;
  if (!d) return <div className="space-y-4"><Skeleton className="h-40" /><Skeleton className="h-64" /></div>;

  const L = d.lender, loan = d.loan;
  const W = d.wallet || {};
  const selOpt = d.options.find((o) => o.type === sel);
  const customVal = Number(custom) || 0;
  const canPay = sel === "CUSTOM" ? customVal > 0 : selOpt?.available;
  const payable = sel === "CUSTOM" ? customVal : (selOpt?.final ?? 0);
  const walletEligible = !!W.eligible && payable > 0;
  const walletApplied = useWallet && walletEligible ? Math.min(W.available_balance || 0, payable) : 0;
  const C = d.coins || {};
  const cashAfterWallet = Math.max(0, payable - walletApplied);
  const coinsEligible = !!C.eligible && cashAfterWallet > 0;
  const coinRupees = useCoins && coinsEligible ? Math.min(C.rupee_value || 0, cashAfterWallet) : 0;
  const coinsToUse = Math.floor(coinRupees * (C.coins_per_rupee || 1000));
  const RC = d.reward_cash || {};
  const cashAfterCoins = Math.max(0, cashAfterWallet - coinRupees);
  const rcEligible = !!RC.eligible && cashAfterCoins > 0;
  const rcApplied = useRewardCash && rcEligible ? Math.min(RC.balance || 0, cashAfterCoins) : 0;
  const cashDue = Math.max(0, cashAfterCoins - rcApplied);

  const pay = async () => {
    setBusy(true);
    try {
      const body = { loan_id: loan.id, method, outcome, payment_type: sel };
      body.idempotency_key = (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : `${loan.id}-${Date.now()}-${Math.random()}`;
      if (sel === "CUSTOM") body.amount = customVal;
      if (useWallet && walletEligible) { body.use_wallet = true; body.wallet_amount = walletApplied; }
      if (useCoins && coinsEligible && coinsToUse > 0) { body.use_coins = true; body.coins_amount = coinsToUse; }
      if (useRewardCash && rcEligible && rcApplied > 0) { body.use_reward_cash = true; body.reward_cash_amount = rcApplied; }
      const { data } = await api.post("/payments/pay", body);
      if (data.status === "SUCCESS") nvg("/payment/success", { state: { payment: data, loan } });
      else if (data.status === "PENDING") nvg("/payment/success", { state: { payment: data, loan, pending: true } });
      else nvg("/payment/failed", { state: { payment: data, loan } });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const lenderRow = (k, v, money = true) => (
    <div className="flex items-center justify-between py-1.5 text-sm">
      <span className="text-slate-500">{k}</span>
      <span className={v === null || v === undefined ? "text-xs italic text-slate-400" : "font-semibold text-[#0B132B]"}>{naFmt(v, money)}</span>
    </div>
  );

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <Link to={`/loans/${loan.id}`} className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-[#0B132B]"><ArrowLeft size={15} /> Back to loan</Link>

      {/* Loan + lender summary */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="pay-loan-summary">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="font-mono text-xs font-semibold text-[#14B8A6]">{loan.loan_number}</p>
            <h1 className="font-display text-xl font-bold text-[#0B132B]">{loan.lender} <span className="text-sm font-normal text-slate-400">{loan.loan_type}</span></h1>
          </div>
          <div className="text-right">
            {L.is_demo && <span className="rounded bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-500">SAMPLE</span>}
            <p className="mt-1 text-xs text-slate-400">{L.has_lender_data ? `Source: ${L.source || "Lender"} • Synced ${L.last_synced_at ? fmtDateTime(L.last_synced_at) : "—"}` : "No lender sync yet"}</p>
          </div>
        </div>
        <div className="mt-4 grid gap-x-8 gap-y-1 sm:grid-cols-2">
          {lenderRow("Principal Outstanding (POS)", L.pos)}
          {lenderRow("Total Outstanding", loan.outstanding)}
          {lenderRow("EMI Amount", loan.emi_amount)}
          {lenderRow("Overdue Amount", L.overdue)}
          {lenderRow("Next EMI Date", L.next_emi_date ? fmtDate(L.next_emi_date) : null, false)}
          {lenderRow("Loan Account", L.loan_account_number, false)}
          {lenderRow("DPD", L.dpd, false)}
          {lenderRow("Bucket", L.bucket, false)}
          {lenderRow("Loan Status", L.loan_status, false)}
          {lenderRow("Settlement Amount", L.settlement_amount)}
          {lenderRow("Closure Amount", L.closure_amount)}
        </div>
        {L.expected_status && (
          <div className="mt-3 rounded-lg bg-teal-50 p-3 text-sm text-[#0B132B]" data-testid="lender-expected">
            <b>Lender-provided:</b> expected status <b>{L.expected_status}</b> after applicable payment. <span className="text-slate-500">Source: {L.source}.</span>
          </div>
        )}
      </div>

      {/* Payment options */}
      <div>
        <h2 className="mb-2 font-display text-lg font-bold text-[#0B132B]">How would you like to pay?</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          {d.options.map((o) => {
            const M = TYPE_META[o.type]; const Icon = M.icon; const active = sel === o.type;
            const disabled = o.type !== "CUSTOM" && !o.available;
            return (
              <button key={o.type} disabled={disabled} onClick={() => setSel(o.type)} data-testid={`pay-opt-${o.type}`}
                className={`rounded-xl border p-4 text-left transition-all ${active ? "border-[#14B8A6] ring-2 ring-[#14B8A6]/30" : "border-slate-200 hover:border-slate-300"} ${disabled ? "cursor-not-allowed opacity-50" : "bg-white"}`}>
                <div className="flex items-center gap-2"><span className="grid h-8 w-8 place-items-center rounded-lg bg-[#14B8A6]/10 text-[#14B8A6]"><Icon size={16} /></span><span className="font-semibold text-[#0B132B]">{M.label}</span></div>
                <p className="mt-1 text-xs text-slate-400">{M.desc}</p>
                {o.type === "CUSTOM" ? (
                  <p className="mt-2 text-sm text-slate-500">Enter any amount below</p>
                ) : disabled ? (
                  <p className="mt-2 text-xs italic text-slate-400">{o.note || "Not available from lender"}</p>
                ) : (
                  <div className="mt-2 text-sm">
                    <div className="flex justify-between"><span className="text-slate-500">Amount</span><span className="font-medium text-[#0B132B]">{inr(o.base)}</span></div>
                    {o.discount > 0 && <div className="flex justify-between text-emerald-600"><span>Offer{o.offer ? ` (${o.offer})` : ""}</span><span>− {inr(o.discount)}</span></div>}
                    <div className="mt-1 flex justify-between border-t border-slate-100 pt-1"><span className="font-semibold text-slate-600">You pay</span><span className="font-display font-bold text-[#0B132B]">{inr(o.final)}</span></div>
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {sel === "CUSTOM" && (
          <div className="mt-3 rounded-xl border border-slate-200 bg-white p-4">
            <label className="text-xs font-medium text-slate-500">Enter amount (₹)</label>
            <input type="number" min="1" value={custom} onChange={(e) => setCustom(e.target.value)} data-testid="pay-custom-input"
              placeholder="e.g. 5000" className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#14B8A6]" />
            <p className="mt-2 text-xs text-slate-400">Applicable offer is applied at checkout. A custom or partial payment does not by itself close, settle, or change your loan status.</p>
          </div>
        )}
      </div>

      {/* ZRo Pe Wallet */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="wallet-panel">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="grid h-9 w-9 place-items-center rounded-lg bg-[#0B132B] text-[#14B8A6]"><Wallet size={18} /></span>
            <div>
              <p className="font-display font-bold text-[#0B132B]">ZRo Pe Wallet</p>
              <p className="text-xs text-slate-500">Available <b className="text-[#0B132B]" data-testid="wallet-available">{inr(W.available_balance || 0)}</b>
                {(W.pending_balance || 0) > 0 && <span className="text-amber-600"> • {inr(W.pending_balance)} pending</span>}</p>
            </div>
          </div>
          {walletEligible ? (
            <button role="switch" aria-checked={useWallet} onClick={() => setUseWallet((v) => !v)} data-testid="wallet-toggle"
              className={`relative h-6 w-11 rounded-full transition-colors ${useWallet ? "bg-[#14B8A6]" : "bg-slate-300"}`}>
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${useWallet ? "translate-x-5" : "translate-x-0.5"}`} />
            </button>
          ) : (
            <span className="rounded bg-slate-100 px-2 py-1 text-[10px] font-semibold text-slate-500">Unavailable</span>
          )}
        </div>
        {!walletEligible && W.reason && <p className="mt-2 text-xs italic text-slate-400" data-testid="wallet-reason">{W.reason}</p>}
        {useWallet && walletApplied > 0 && (
          <div className="mt-3 space-y-1 rounded-lg bg-emerald-50 p-3 text-sm" data-testid="wallet-split">
            <div className="flex justify-between text-slate-600"><span>Total payable</span><span className="font-semibold text-[#0B132B]">{inr(payable)}</span></div>
            <div className="flex justify-between text-[#14B8A6]"><span>From wallet</span><span>− {inr(walletApplied)}</span></div>
            <div className="flex justify-between border-t border-emerald-100 pt-1 font-semibold text-[#0B132B]"><span>Pay via gateway</span><span>{inr(cashDue)}</span></div>
            <p className="pt-1 text-[11px] text-slate-500">The lender still receives the full {inr(payable)}. Wallet only funds your share.</p>
          </div>
        )}
      </div>

      {/* ZRo Pe Coins */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="coins-panel">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CoinIcon size={38} />
            <div>
              <p className="font-display font-bold text-[#0B132B]">Use ZRo Pe Coins</p>
              <p className="text-xs text-slate-500">Available <b className="text-[#0B132B]" data-testid="coins-available">{(C.balance || 0).toLocaleString("en-IN")} Coins</b>
                <span className="text-slate-400"> • ≈ {inr(C.rupee_value || 0)}</span></p>
            </div>
          </div>
          {coinsEligible ? (
            <button role="switch" aria-checked={useCoins} onClick={() => setUseCoins((v) => !v)} data-testid="coins-toggle"
              className={`relative h-6 w-11 rounded-full transition-colors ${useCoins ? "bg-[#F5B301]" : "bg-slate-300"}`}>
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${useCoins ? "translate-x-5" : "translate-x-0.5"}`} />
            </button>
          ) : (
            <span className="rounded bg-slate-100 px-2 py-1 text-[10px] font-semibold text-slate-500">Unavailable</span>
          )}
        </div>
        {!coinsEligible && C.reason && <p className="mt-2 text-xs italic text-slate-400" data-testid="coins-reason">{C.reason}</p>}
        {useCoins && coinRupees > 0 && (
          <div className="mt-3 space-y-1 rounded-lg bg-amber-50 p-3 text-sm" data-testid="coins-split">
            <div className="flex justify-between text-slate-600"><span>Payable after wallet</span><span className="font-semibold text-[#0B132B]">{inr(cashAfterWallet)}</span></div>
            <div className="flex justify-between text-[#B45309]"><span>{coinsToUse.toLocaleString("en-IN")} ZRo Pe Coins</span><span>− {inr(coinRupees)}</span></div>
            <div className="flex justify-between border-t border-amber-100 pt-1 font-semibold text-[#0B132B]"><span>Pay via gateway</span><span>{inr(cashDue)}</span></div>
            <p className="pt-1 text-[11px] text-slate-500">Coins are deducted securely on the server. The lender still receives the full {inr(payable)}. ZRo Pe Coins are non-withdrawable and can only be used toward eligible payments.</p>
          </div>
        )}
      </div>

      {/* Reward Cash (Direct Rupee Reward) */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="reward-cash-panel">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="grid h-9 w-9 place-items-center rounded-full bg-emerald-100 text-emerald-700"><CircleDollarSign size={20} /></span>
            <div>
              <p className="font-display font-bold text-[#0B132B]">Use Reward Cash</p>
              <p className="text-xs text-slate-500">Available <b className="text-[#0B132B]" data-testid="reward-cash-available">{inr(RC.balance || 0)}</b>
                <span className="text-slate-400"> • Direct ₹ reward</span></p>
            </div>
          </div>
          {rcEligible ? (
            <button role="switch" aria-checked={useRewardCash} onClick={() => setUseRewardCash((v) => !v)} data-testid="reward-cash-toggle"
              className={`relative h-6 w-11 rounded-full transition-colors ${useRewardCash ? "bg-emerald-500" : "bg-slate-300"}`}>
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${useRewardCash ? "translate-x-5" : "translate-x-0.5"}`} />
            </button>
          ) : (
            <span className="rounded bg-slate-100 px-2 py-1 text-[10px] font-semibold text-slate-500">Unavailable</span>
          )}
        </div>
        {!rcEligible && RC.reason && <p className="mt-2 text-xs italic text-slate-400" data-testid="reward-cash-reason">{RC.reason}</p>}
        {useRewardCash && rcApplied > 0 && (
          <div className="mt-3 space-y-1 rounded-lg bg-emerald-50 p-3 text-sm" data-testid="reward-cash-split">
            <div className="flex justify-between text-emerald-800"><span>Reward Cash applied</span><span>− {inr(rcApplied)}</span></div>
            <div className="flex justify-between border-t border-emerald-100 pt-1 font-semibold text-[#0B132B]"><span>Pay via gateway</span><span>{inr(cashDue)}</span></div>
            <p className="pt-1 text-[11px] text-slate-500">Reward Cash is deducted securely on the server. It is non-withdrawable and can only reduce an eligible payment. The lender still receives the full {inr(payable)}.</p>
          </div>
        )}
      </div>

      {/* Method + demo outcome + pay */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="text-xs font-medium text-slate-500">Payment method</label>
            <select value={method} onChange={(e) => setMethod(e.target.value)} data-testid="pay-method-select" className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#14B8A6]">
              {["UPI_INTENT", "CARD", "NETBANKING"].map((m) => <option key={m} value={m}>{m.replace(/_/g, " ")}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500">Payment outcome</label>
            <select value={outcome} onChange={(e) => setOutcome(e.target.value)} data-testid="pay-outcome-select" className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#14B8A6]">
              {["success", "pending", "failed"].map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        </div>
        <button onClick={pay} disabled={!canPay || busy} data-testid="pay-confirm-btn"
          className="mt-4 w-full rounded-md bg-[#14B8A6] px-5 py-3 text-sm font-bold text-white hover:bg-[#0D9488] disabled:opacity-50">
          {busy ? "Processing…" : `Pay ${payable ? inr(cashDue) : (sel === "CUSTOM" ? "custom amount" : "")}`}{useWallet && walletApplied > 0 ? " via gateway" : ""}
        </button>
        <div className="mt-3 flex items-start gap-2 rounded-lg bg-slate-50 p-3 text-xs text-slate-500" data-testid="pay-disclaimer">
          <ShieldCheck size={15} className="mt-0.5 shrink-0 text-[#14B8A6]" /><span>{d.disclaimer}</span>
        </div>
      </div>
    </div>
  );
}
